import { Link, useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  getThreads,
  getNotifications,
  markNotificationsRead,
  deleteThread,
  deleteNotification,
  type ThreadListItem,
} from '../../api/messenger'
import { useInboxWS } from '../../hooks/useInboxWS'
import NavBar from '../../components/layout/NavBar'
import { useLocation } from 'react-router-dom'

export default function Inbox() {
  const location = useLocation()
  const navigate = useNavigate()
  // WS for live updates (thread:update, notification:new)
  const { lastUpdate } = useInboxWS()

  const toISO = (v?: string | null) => {
    const d = v ? new Date(v) : new Date()
    return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString()
  }
  const formatDate = (v?: string | null) => {
    const d = v ? new Date(v) : null
    return d && !isNaN(d.getTime()) ? d.toLocaleString() : ''
  }
  const formatRelative = (v?: string | null) => {
    const d = v ? new Date(v) : null
    if (!d || isNaN(d.getTime())) return ''
    const diff = Date.now() - d.getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    const days = Math.floor(hrs / 24)
    if (days < 7) return `${days}d ago`
    return d.toLocaleDateString()
  }
  const initialsFor = (name?: string) => {
    const c = (name || '').trim()
    if (!c) return '?'
    const parts = c.split(/\s+/)
    if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase()
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  const notificationInitials = (n: any) => {
    if (n?.actor?.name) return initialsFor(n.actor.name)
    if (n?.type) return n.type.toString().slice(0, 2).toUpperCase()
    if (n?.category) return n.category.toString().slice(0, 2).toUpperCase()
    return 'SM'
  }
  const placeholderColor = (id: string | number) => {
    const palette = ['bg-emerald-600', 'bg-sky-600', 'bg-amber-600', 'bg-fuchsia-600', 'bg-indigo-600', 'bg-rose-600']
    const idx = Math.abs(Number(String(id).charCodeAt(0) || 0)) % palette.length
    return palette[idx]
  }
  const sectionLabelFor = (iso?: string | null) => {
    if (!iso) return 'Earlier'
    const d = new Date(iso)
    if (isNaN(d.getTime())) return 'Earlier'
    const now = new Date()
    const sameDay = d.toDateString() === now.toDateString()
    if (sameDay) return 'Today'
    const yesterday = new Date(now)
    yesterday.setDate(now.getDate() - 1)
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24))
    if (diffDays < 7) return 'This week'
    const formatter = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    return formatter.format(d)
  }

  // Raw pages
  const [threads, setThreads] = useState<ThreadListItem[] | null>(null)
  const [threadsNext, setThreadsNext] = useState<string | null>(null)
  const [notifs, setNotifs] = useState<any[] | null>(null)
  const [notifsNext, setNotifsNext] = useState<string | null>(null)
  const [activeSystem, setActiveSystem] = useState<any | null>(null)

  // UI state
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const sp = useMemo(() => new URLSearchParams(location.search), [location.search])
  const q = sp.get('q') || ''
  const rawQuery = q.trim()
  type Filter = 'all' | 'messages' | 'mentions' | 'reactions' | 'follows' | 'calls'
  const [filter, setFilter] = useState<Filter>('all')
  const filterOptions: Array<{ key: Filter; label: string }> = useMemo(
    () => [
      { key: 'all', label: 'All' },
      { key: 'messages', label: 'Chats' },
      { key: 'mentions', label: 'Mentions' },
      { key: 'reactions', label: 'Reactions' },
      { key: 'follows', label: 'Follows' },
      { key: 'calls', label: 'Calls' },
    ],
    []
  )

  const load = useCallback(async (initial = false) => {
    if (loading) return
    const shouldFetchThreads = initial || threads == null || !!threadsNext
    const shouldFetchNotifs = initial || notifs == null || !!notifsNext
    if (!shouldFetchThreads && !shouldFetchNotifs) return
    setLoading(true)
    setError(null)
    try {
      const [tPage, nPage] = await Promise.all([
        shouldFetchThreads ? getThreads(initial ? undefined : threadsNext ?? undefined, rawQuery ? { q: rawQuery } : undefined) : Promise.resolve(null),
        shouldFetchNotifs
          ? getNotifications(
              initial ? undefined : notifsNext ?? undefined,
              {
                q: rawQuery || undefined,
                type:
                  filter === 'mentions' ? 'mention' :
                  filter === 'reactions' ? 'reaction' :
                  filter === 'follows' ? 'follow' :
                  filter === 'calls' ? 'call' : undefined
              }
            )
          : Promise.resolve(null),
      ])
      if (tPage) {
        if (initial || threads == null) {
          setThreads(tPage.results)
        } else {
          setThreads([...(threads ?? []), ...tPage.results])
        }
        setThreadsNext(tPage.next)
      }
      if (nPage) {
        if (initial || notifs == null) {
          setNotifs(nPage.results)
        } else {
          setNotifs([...(notifs ?? []), ...nPage.results])
        }
        setNotifsNext(nPage.next)
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load inbox')
    } finally {
      setLoading(false)
    }
  }, [loading, threadsNext, notifsNext, threads, notifs])

  useEffect(() => {
    // reset pages when query changes
    setThreads(null)
    setThreadsNext(null)
    setNotifs(null)
    setNotifsNext(null)
    void load(true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawQuery])

  // Light auto-refresh on WS updates
  useEffect(() => {
    if (!lastUpdate) return
    const t = setTimeout(() => void load(true), 150)
    return () => clearTimeout(t)
  }, [lastUpdate, load])

  // Merge + normalize
  const query = q.trim().toLowerCase()
  const merged = useMemo(() => {
    const T = (threads ?? []).map((t) => ({
      kind: 'thread' as const,
      id: String(t.id),
      title: t.title || t.dmPeer?.name || 'Conversation',
      last: t.lastMessage?.text || '',
      unread: t.unreadCount || 0,
      updatedAt: t.updatedAt,
      raw: t,
    }))
    // Signup/login OTP notifications (event_type=login.code) should be email-only, not clutter Inbox.
    const N = (notifs ?? [])
      .filter((n: any) => String(n?.eventType || '').toLowerCase() !== 'login.code')
      .map((n: any) => ({
      kind: 'system' as const,
      id: String(n.id),
      type: n.type as string,
      verb: n.verb,
      preview: n.preview,
      deeplink: n.deeplink,
      category: n.category,
      actor: n.actor,
      read: !!n.read,
      updatedAt: toISO(n.created_at || n.createdAt),
      raw: n,
    }))
    const arr = [...T, ...N]
    arr.sort((a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    return query
      ? arr.filter((it) => {
          if (it.kind === 'thread') {
            return (
              (it.title || '').toLowerCase().includes(query) ||
              (it.last || '').toLowerCase().includes(query)
            )
          }
          const actor = (it.actor?.name || '').toLowerCase()
          return actor.includes(query) || (it.type || '').toLowerCase().includes(query)
        })
      : arr
  }, [threads, notifs, query])

  const filtered = useMemo(() => {
    if (filter === 'all') return merged
    if (filter === 'messages') return merged.filter((i: any) => i.kind === 'thread')
    if (filter === 'mentions') return merged.filter((i: any) => i.kind === 'system' && i.type === 'mention')
    if (filter === 'reactions') return merged.filter((i: any) => i.kind === 'system' && i.type === 'reaction')
    if (filter === 'follows') return merged.filter((i: any) => i.kind === 'system' && i.type === 'follow')
    if (filter === 'calls') return merged.filter((i: any) => i.kind === 'system' && String(i.type || '').startsWith('call'))
    return merged
  }, [merged, filter])

  const sections = useMemo(() => {
    const groups: Array<{ label: string; items: any[] }> = []
    for (const item of filtered) {
      const label = sectionLabelFor(item.updatedAt)
      const last = groups[groups.length - 1]
      if (last && last.label === label) {
        last.items.push(item)
      } else {
        groups.push({ label, items: [item] })
      }
    }
    return groups
  }, [filtered])

  const unreadNotifs = useMemo(() => (notifs ?? []).filter((n: any) => !n.read).length, [notifs])

  const onMarkAll = useCallback(async () => {
    const ids = (notifs ?? []).filter((n: any) => !n.read).map((n: any) => n.id)
    if (!ids.length) return
    try {
      await markNotificationsRead(ids)
      // Optimistic update
      setNotifs((prev) => (prev ?? []).map((n: any) => (ids.includes(n.id) ? { ...n, read: true } : n)))
    } catch {}
  }, [notifs])

  const onDeleteAll = useCallback(async () => {
    const total = (threads?.length ?? 0) + (notifs?.length ?? 0)
    if (!total) return
    const ok = window.confirm('Delete all conversations and notifications? This cannot be undone.')
    if (!ok) return
    const threadIds = (threads ?? []).map((t) => t.id)
    const notifIds = (notifs ?? []).map((n: any) => n.id)
    // Optimistic UI
    setThreads([])
    setNotifs([])
    try {
      await Promise.all([
        Promise.all(threadIds.map((id) => deleteThread(id).catch(() => undefined))),
        Promise.all(notifIds.map((id) => deleteNotification(id).catch(() => undefined))),
      ])
    } catch {
      // Reload to resync if something failed
      void load(true)
    }
  }, [threads, notifs])

  const onSystemClick = useCallback(async (item: any) => {
    setActiveSystem(item)
    if (!item.read) {
      setNotifs((prev) => (prev ?? []).map((n: any) => (String(n.id) === String(item.id) ? { ...n, read: true } : n)))
      try {
        await markNotificationsRead([item.id])
      } catch {}
    }
    const deeplink = item.deeplink || item.raw?.deeplink
    if (deeplink) {
      if (deeplink.startsWith('http')) {
        window.open(deeplink, '_blank')
      } else {
        navigate(deeplink)
      }
    }
  }, [navigate])

  return (
    <div className="min-h-screen">
      <NavBar />

      <main className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-extrabold">Inbox</h1>
          <div className="flex items-center gap-2 text-xs">
            {filterOptions.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`rounded-full px-3 py-1.5 border transition ${
                  filter===f.key
                    ? 'bg-brand text-white border-brand/80 shadow-sm'
                    : 'bg-white/5 border-white/10 text-white/80 hover:bg-white/10'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => void onMarkAll()}
              disabled={unreadNotifs === 0}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold hover:bg-white/10 disabled:opacity-50"
            >
              Mark notifications read{unreadNotifs ? ` (${unreadNotifs})` : ''}
            </button>
            <button
              onClick={() => void onDeleteAll()}
              disabled={(threads?.length ?? 0) + (notifs?.length ?? 0) === 0}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold hover:bg-white/10 disabled:opacity-50"
            >
              Delete all
            </button>
            <Link to="/inbox/compose" className="inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600">Compose</Link>
          </div>
        </div>

        <div className="mb-4 flex items-center gap-2 justify-between">
          <div className="text-sm text-white/60">All conversations and system updates</div>
          <button
            onClick={() => void load(true)}
            className="rounded-full border border-white/15 bg-white/5 px-3 py-2 text-sm font-semibold hover:bg-white/10"
          >
            Refresh
          </button>
        </div>

        {activeSystem && (
          <div className="mb-4 rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-start gap-3">
              <div className="flex flex-col gap-1">
                <div className="inline-flex items-center gap-2">
                  <span className="rounded-full border border-white/10 bg-white/10 px-2 py-0.5 text-[11px] font-bold uppercase text-white/90">{activeSystem.type || 'System'}</span>
                  {activeSystem.category && (
                    <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] font-semibold text-white/70">{activeSystem.category}</span>
                  )}
                </div>
                <div className="text-xs text-white/60" title={formatDate(activeSystem.updatedAt)}>{formatRelative(activeSystem.updatedAt)}</div>
              </div>
              <button onClick={() => setActiveSystem(null)} className="ml-auto text-xs rounded-full border border-white/10 bg-white/5 px-2 py-1 hover:bg-white/10">Close</button>
            </div>
            <div className="mt-3 text-sm text-white/90">
              <div className="font-semibold mb-1">{activeSystem.verb || 'Update'}</div>
              <div className="text-white/70">{activeSystem.preview || 'No additional details provided.'}</div>
              {activeSystem.actor?.name && (
                <div className="mt-2 text-xs text-white/60">From: {activeSystem.actor.name}</div>
              )}
            </div>
            {(activeSystem.deeplink || activeSystem.raw?.deeplink) && (
              <div className="mt-3">
                <button
                  onClick={() => onSystemClick(activeSystem)}
                  className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-sm font-semibold hover:bg-white/10"
                >
                  Open related page
                </button>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-sm font-semibold text-red-300">{error}</div>
            <div className="text-xs text-white/60 mt-1">We couldn’t load your inbox. Please try again.</div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => void load(true)}
                className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold hover:bg-white/10"
              >
                Retry
              </button>
              <button
                onClick={() => setError(null)}
                className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold hover:bg-white/10"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 rounded-xl border border-white/10 bg-white/5 overflow-hidden">
          {loading && !threads && !notifs && !error && (
            <div className="divide-y divide-white/10">
              {[0,1,2,3].map((i) => (
                <div key={i} className="p-4 flex items-center gap-3 animate-pulse">
                  <div className="h-10 w-10 rounded-full bg-white/10" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-32 bg-white/10 rounded-full" />
                    <div className="h-3 w-48 bg-white/5 rounded-full" />
                  </div>
                  <div className="h-3 w-16 bg-white/10 rounded-full" />
                </div>
              ))}
            </div>
          )}
          {!loading && filtered.length === 0 && !error && (
            <div className="p-6 text-center text-white/70">
              <div className="text-lg font-semibold mb-1">Your inbox is clear</div>
              <div className="text-sm mb-3">Start a new conversation or wait for updates.</div>
              <div className="flex justify-center gap-2">
                <Link to="/inbox/compose" className="inline-flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600">Compose</Link>
                <button
                  onClick={() => void load(true)}
                  className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold hover:bg-white/10"
                >
                  Refresh
                </button>
              </div>
            </div>
          )}
          {sections.map((section) => (
            <div key={section.label} className="divide-y divide-white/10">
              <div className="bg-white/5 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-white/60 grid grid-cols-[48px,160px,80px,1fr,120px,80px] items-center gap-2">
                <span className="text-white/40"> </span>
                <span>{section.label}</span>
                <span className="text-white/50 text-[10px] text-left">Type</span>
                <span className="text-[10px] text-white/50 text-left">Message</span>
                <span className="text-right text-[10px] text-white/50 w-[120px]">Date</span>
                <span className="text-[10px] text-white/50 text-right"> </span>
              </div>
              {section.items.map((it: any) =>
                it.kind === 'thread' ? (
                  <Link
                    key={`t:${it.id}`}
                    to={`/inbox/i/${it.id}`}
                    className={`group p-3 hover:bg-white/5 grid grid-cols-[48px,160px,80px,1fr,120px,80px] items-center gap-2 border-l-2 ${it.unread>0 ? 'border-brand' : 'border-transparent'}`}
                  >
                    <div className="w-12 flex items-center justify-center">
                      {it.raw?.dmPeer?.avatarUrl ? (
                        <img src={it.raw.dmPeer.avatarUrl} className="h-10 w-10 rounded-full object-cover border border-white/20" />
                      ) : (
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-semibold text-white/90 ${placeholderColor(it.id)}`}>
                          {initialsFor(it.title)}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className={`truncate ${it.unread>0 ? 'font-extrabold text-white' : 'font-semibold'}`}>{it.title}</div>
                      {it.unread > 0 && (
                        <span className="mt-1 inline-flex items-center rounded-full bg-brand px-1.5 py-0.5 text-[10px] font-bold text-white">Unread {it.unread}</span>
                      )}
                    </div>
                    <div className="min-w-0 text-left">
                      <span className="inline-flex items-center rounded-full border border-brand/40 bg-brand/80 px-2 py-0.5 text-[10px] font-bold uppercase text-white">Chat</span>
                    </div>
                    <div className="min-w-0 text-left">
                      <div className={`text-sm truncate ${it.unread>0 ? 'text-white/90 font-semibold' : 'text-white/70'}`}>{it.last}</div>
                    </div>
                    <div className="text-xs text-white/60 whitespace-nowrap text-right w-[120px]" title={formatDate(it.updatedAt)}>
                      {formatRelative(it.updatedAt)}
                      {it.unread > 0 && <span className="inline-block h-2 w-2 rounded-full bg-brand ml-1 align-middle" aria-hidden />}
                    </div>
                    <div className="flex items-center justify-end">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          // Optimistic delete
                          setThreads((prev) => (prev ?? []).filter((t) => String(t.id) !== String(it.id)))
                          void deleteThread(it.id).catch(() => void load(true))
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-xs rounded-lg border border-white/15 bg-white/5 px-2 py-1 hover:bg-white/10"
                      >
                        Archive
                      </button>
                    </div>
                  </Link>
                ) : (
                  <Link
                    key={`n:${it.id}`}
                    to={`/inbox/n/${it.id}`}
                    state={{ notif: it.raw }}
                    onClick={() => {
                      // mark as read optimistically
                      if (!it.read) {
                        setNotifs((prev) => (prev ?? []).map((n: any) => (String(n.id) === String(it.id) ? { ...n, read: true } : n)))
                        void markNotificationsRead([it.id]).catch(() => {})
                      }
                    }}
                    className="group p-3 grid grid-cols-[48px,160px,80px,1fr,120px,80px] items-center gap-2 w-full text-left hover:bg-white/5"
                  >
                    <div className="w-12 flex items-center justify-center">
                      {it.actor?.avatarUrl ? (
                        <img src={it.actor.avatarUrl} className="h-8 w-8 rounded-full object-cover border border-white/20" />
                      ) : (
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold text-white/90 ${placeholderColor(it.id)}`}>
                          {notificationInitials(it)}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{it.actor?.name || 'System'}</div>
                    </div>
                    <div className="min-w-0 text-left">
                      <span className="inline-flex items-center rounded-full border border-white/10 bg-white/10 px-2 py-0.5 text-[10px] font-bold uppercase text-white/80">{it.type || 'Activity'}</span>
                    </div>
                    <div className="min-w-0 text-left">
                      <div className="text-xs text-white/70 truncate">{it.verb || it.preview || ''}</div>
                    </div>
                    <div className="text-[11px] text-white/60 whitespace-nowrap text-right w-[120px]" title={formatDate(it.updatedAt)}>{formatRelative(it.updatedAt)}</div>
                    <div className="flex items-center justify-end">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          // Optimistic delete
                          setNotifs((prev) => (prev ?? []).filter((n: any) => String(n.id) !== String(it.id)))
                          void deleteNotification(it.id).catch(() => void load(true))
                        }}
                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-xs rounded-lg border border-white/15 bg-white/5 px-2 py-1 hover:bg-white/10"
                        aria-label="Dismiss notification"
                      >
                        Dismiss
                      </button>
                    </div>
                  </Link>
                )
              )}
            </div>
          ))}
        </div>

        {(threadsNext || notifsNext) && (
          <div className="flex justify-center mt-4">
            <button
              onClick={() => void load(false)}
              disabled={loading}
              className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold hover:bg-white/10 disabled:opacity-60"
            >
              {loading ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </main>
    </div>
  )
}
